import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/notificationSidebar.css';
import { getNotificationsApi, ApiNotificationItem, ApiNotificationMessage, NotificationItem } from './notificationData.type';
// Add import at the top
import EmptyNotification from '../../../../dashboard-fs-cp/notification-dsb-op/empty-notification/ts/emptyNotification';
/* ── Extended local type ─────────────────────────────────── */
interface NotifRow extends NotificationItem {
  module: string;
}

export interface NotificationSidebarProps {
  visible?: boolean;
  onSeeMore?: () => void;
  realtimeItem?: ApiNotificationItem | null;
}

/* ── Helpers ─────────────────────────────────────────────── */

function formatDate(raw: string): string {
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    /* istanbul ignore next */
    return raw;
  }
}

function parseMessage(raw: string): { module: string; message: string } {
  try {
    return JSON.parse(raw.trim());
  } catch {
    /* istanbul ignore next */
    return { module: '', message: raw };
  }
}

const MODULE_ROUTES: Record<string, string> = {
  workflow: '/workflow',
  media: '/media',
  career: '/career/hire',
  inbox: '/inbox',
  setting: '/setting',
  content: '/content'
};

function getModuleRoute(module: string): string {
  return MODULE_ROUTES[module.toLowerCase()] ?? '/';
}

// ✅ Fix #3 — matches notificationTable mapApiItem, removes startsWith guard
function mapApiItem(item: ApiNotificationItem): NotifRow {
  const parsed: ApiNotificationMessage =
    typeof item.message === 'object' && item.message !== null
      ? item.message as ApiNotificationMessage //NOSONAR
      : parseMessage(String(item.message ?? ''));

  return {
    id: String(item.id),
    date: formatDate(item.createdAt),
    title: parsed.module ?? /* istanbul ignore next */ '',
    description: parsed.message ?? /* istanbul ignore next */ '',
    module: parsed.module ?? /* istanbul ignore next */ '',
  };
}

function extractItems(res: any): ApiNotificationItem[] {
  const d = res?.data;
  /* istanbul ignore next */
  if (Array.isArray(d))        return d;
  if (Array.isArray(d?.data))  return d.data;
  /* istanbul ignore next */
  if (Array.isArray(d?.items)) return d.items;
  /* istanbul ignore next */
  if (Array.isArray(res?.items)) return res.items;
  /* istanbul ignore next */
  return [];
}

/* ── Sub-component ───────────────────────────────────────── */

const NotificationRow: React.FC<{ item: NotifRow; onNavigate: (module: string) => void }> = ({
  item,
  onNavigate,
}) => (
  <div className="scl--cms-components-notifSidebar-row">
    <div className="scl--cms-components-notifSidebar-text">
      <span //NOSONAR
        className="scl--cms-components-notifSidebar-title"
        onClick={() => onNavigate(item.module)}
      >
        {item.title}
      </span>
      <p className="scl--cms-components-notifSidebar-body">
        {item.description}
      </p>
    </div>
  </div>
);

/* ── Main component ──────────────────────────────────────── */

/* istanbul ignore next */
const NotificationSidebar: React.FC<NotificationSidebarProps> = ({
  visible = true,
  onSeeMore,
  realtimeItem,
}) => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<NotifRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef<HTMLDivElement | null>(null);
  const PAGE_SIZE = 20;

  /* istanbul ignore next */
  const fetchNotifications = useCallback(async (pageNumber = 1, isLoadMore = false) => {
    try {
      isLoadMore ? setLoadingMore(true) : setLoading(true);
      setError(false);

      const res = await getNotificationsApi(pageNumber, PAGE_SIZE);
      const apiItems = extractItems(res);

      // ✅ Fix #1 — show all items, no UNREAD filter
      const mappedItems = apiItems.map(mapApiItem);

      setNotifications((prev) => isLoadMore ? [...prev, ...mappedItems] : mappedItems);
      setHasMore(apiItems.length === PAGE_SIZE);
    } catch (err) { //NOSONAR
      setError(true);
      if (!isLoadMore) setNotifications([]);
    } finally {
      isLoadMore ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    setPage(1);
    setHasMore(true);
    setNotifications([]);
    fetchNotifications(1, false);
    // ← removed setTimeout scroll reset, handled by listCallbackRef
  }, [visible, fetchNotifications]);

  // ── Load more on page change ───────────────────────────
  useEffect(() => {
    if (page === 1) return;
    fetchNotifications(page, true);
  }, [page, fetchNotifications]);

  // ── Prepend realtime item with 5s delay ────────────────
  useEffect(() => {
    if (!realtimeItem) return;

    const mapped = mapApiItem(realtimeItem);

    // ✅ Fix #1 — removed UNREAD guard, consistent with fetch
    const t = setTimeout(() => {
      setNotifications((prev) => {
        if (prev.some((n) => n.id === mapped.id)) return prev; //NOSONAR
        return [mapped, ...prev].slice(0, PAGE_SIZE);
      });
    });

    return () => clearTimeout(t);
  }, [realtimeItem]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    /* istanbul ignore next */
    if (!el || loading || loadingMore || !hasMore) return;
    const reachedBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 20;
    /* istanbul ignore next */
    if (reachedBottom) setPage((prev) => prev + 1);
  }, [loading, loadingMore, hasMore]);

  // ── Guard — must be AFTER all hooks ───────────────────
  if (!visible) return null;
  const listCallbackRef = useCallback((node: HTMLDivElement | null) => { //NOSONAR
  listRef.current = node;
  if (node) {
    node.scrollTop = 0; // ← fires immediately when DOM is attached
  }
}, []);

  return (
    <div className="scl--cms-components-notifSidebar">

      {/* ── Header ── */}
      <div className="scl--cms-components-notifSidebar-header">
        <span className="scl--cms-components-notifSidebar-headerTitle">
          Notification
        </span>
        <button
          className="scl--cms-components-notifSidebar-seeMore"
          type="button"
          onClick={onSeeMore}
        >
          See more
        </button>
      </div>

      {/* ── List ── */}
      <div
        ref={listCallbackRef}   
        className="scl--cms-components-notifSidebar-list"
        onScroll={handleScroll}
      >
        {loading && !loadingMore ? (
          <div className="scl--cms-components-notifSidebar-empty">Loading…</div>
        ) : error ? ( //NOSONAR
          <div className="scl--cms-components-notifSidebar-empty">Failed to load notifications.</div>
        ) : notifications.length === 0 ? ( //NOSONAR
            <EmptyNotification />
          ) : (
          <>
            {notifications.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                onNavigate={(module) => {
                  onSeeMore?.();
                  navigate(getModuleRoute(module));
                }}
              />
            ))}
            {loadingMore && (
              <div className="scl--cms-components-notifSidebar-empty">Loading more…</div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default NotificationSidebar;